import { useEffect, useState } from 'react';

/* ══════════════════════════════════════════════════════════════════════════
 * SPLASH — `01_Splash.html`
 *
 * The mark spins, the word rises, and Eleat goes where it was always going.
 * Timing and motion are the locked screen's: `spinY 2.8s linear`, then
 * `riseIn .7s ease-out` starting at 2.9s. It leaves at 3.6s.
 *
 * ── ⚠️ IT DECIDES NOTHING. THAT IS THE WHOLE DESIGN ─────────────────────────
 *
 * Al, 22 September 2026: *"if they're already logged in, they go straight to
 * the home page. They bypass the splash, the logins, everything."*
 *
 * So `main.jsx` has already chosen Home or Welcome before this renders — this
 * screen just holds the curtain for three and a half seconds and then hands
 * over to whatever was chosen. A splash that ran its own "where next" check
 * would be a second copy of the front-door rule, and the two would drift.
 *
 * ── ⚠️ ONCE PER APP OPEN, NEVER ONCE PER TAP ────────────────────────────────
 *
 * `sessionStorage`, not `localStorage`. An Eleater who opens Eleat, taps a
 * venue and comes back to Home must not sit through the animation again — but
 * closing the app and opening it tomorrow should feel like opening Eleat, not
 * resuming a browser tab. Session storage is exactly that boundary, and it
 * clears itself.
 *
 * ⚠️ AND IT IS WRAPPED, because storage can be switched off. A brand moment is
 * not worth a white screen: if `sessionStorage` throws, the splash is skipped
 * and Eleat opens. It is decoration with a job, not a gate.
 *
 * ⚠️ NO SKIP BUTTON. Three and a half seconds, once per open, on a screen with
 * nothing to read. A "skip" invites a tap during the one moment Eleat has to
 * look like itself — and anybody in a hurry is through it before they find it.
 * ══════════════════════════════════════════════════════════════════════════ */

const KEY = 'eleat.splash.seen';
const HOLD_MS = 3600;   // 2.8s spin + .7s rise, then gone

/** Has this app-open already shown it? Never throws. */
export function splashSeen() {
  try { return sessionStorage.getItem(KEY) === '1'; } catch { return true; }
}

export default function Splash({ children }) {
  /* ⚠️ THE DECISION IS MADE ONCE, ON THE FIRST RENDER, not in an effect —
     otherwise the app behind it paints for a frame and the splash appears on
     top of a half-drawn Home. */
  const [showing, setShowing] = useState(() => !splashSeen());

  useEffect(() => {
    if (!showing) return;
    try { sessionStorage.setItem(KEY, '1'); } catch { /* fine, it just repeats */ }
    const t = setTimeout(() => setShowing(false), HOLD_MS);
    return () => clearTimeout(t);
  }, [showing]);

  if (!showing) return children;

  return (
    <div className="splash">
      {/* ⚠️ THE REAL MARK AND THE REAL WORD, as two files — the locked screen
          uses `eleat_mark.png` and `eleat_word.png` and animates them
          separately, which is the whole effect. The word typed in a font was
          mine once and it was not Eleat's. */}
      <img className="splashMark" src="/eleat_mark.png" alt="" />
      <img className="splashWord" src="/eleat_word.png" alt="Eleat" />
    </div>
  );
}
